import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { useProfilePasswordReset } from '../../../application/hooks/useProfilePasswordReset';
import { checkPasswordPolicy, passwordStrength } from '../../../domain/validators/passwordPolicy';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { OTPInput } from '../ui/OTPInput';

export interface PasswordResetFlowProps {
  /** The signed-in member's own phone — never typed, so the code can only go to the account holder. */
  phone: string;
  /** Called from the success step: the backend revoked every session, so the host signs out locally. */
  onSignOut: () => void;
  /** Reports an in-flight request so the host can block closing the dialog meanwhile. */
  onBusyChange?: (busy: boolean) => void;
}

const RULES = [
  { rule: 'LENGTH', label: 'auth.forgot.password.ruleLength' },
  { rule: 'LETTER', label: 'auth.forgot.password.ruleLetter' },
  { rule: 'DIGIT', label: 'auth.forgot.password.ruleDigit' },
] as const;

/**
 * Send code → enter code → new password → done, inside the profile Modal / BottomSheet. Mounted only while the
 * dialog is open, so closing it discards the code, reset token and typed passwords with the component.
 */
export function PasswordResetFlow({ phone, onSignOut, onBusyChange }: PasswordResetFlowProps) {
  const { t } = useTranslation();
  const flow = useProfilePasswordReset(phone);
  const { step, notice, pending, blockedIn } = flow;
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [triedSubmit, setTriedSubmit] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => heading.current?.focus(), [step]);
  useEffect(() => {
    onBusyChange?.(pending);
    return () => onBusyChange?.(false);
  }, [pending, onBusyChange]);
  // A wrong / expired code is never reusable; connectivity errors keep what was typed so the member can retry.
  useEffect(() => {
    if (notice && !notice.retryable) setCode('');
  }, [notice]);
  // Token expiry sends the flow back to the start: nothing typed survives.
  useEffect(() => {
    if (step === 'send') {
      setNewPassword('');
      setConfirm('');
      setTriedSubmit(false);
    }
  }, [step]);

  const violations = checkPasswordPolicy(newPassword, phone);
  const strength = passwordStrength(newPassword, phone);
  const mismatch = confirm.length > 0 || triedSubmit ? newPassword !== confirm : false;
  const waiting = blockedIn > 0;
  const masked = `${phone.slice(0, 2)}••••••${phone.slice(-3)}`;

  // The rate-limit notice is shown live from the countdown; once it hits zero the message disappears.
  const noticeText = notice
    ? notice.key === 'profile.password.error.rateWait'
      ? waiting
        ? t(notice.key, { seconds: blockedIn })
        : null
      : t(notice.key, notice.vars)
    : null;

  const submit = (e: FormEvent, run: () => void) => {
    e.preventDefault();
    run();
  };

  const onPassword = () => {
    setTriedSubmit(true);
    if (violations.length > 0 || newPassword !== confirm) return;
    flow.submitPassword(newPassword);
  };

  const noticeBlock = noticeText && (
    <p className="text-danger pw-notice" role="alert" data-testid="pw-error">
      {noticeText}
      {notice?.retryable && <span className="pw-retry-hint"> {t('profile.password.retryHint')}</span>}
    </p>
  );

  return (
    <div className="pw-flow" data-testid="pw-flow" data-step={step}>
      {step === 'send' && (
        <form noValidate onSubmit={(e) => submit(e, flow.sendCode)}>
          <h3 ref={heading} tabIndex={-1} className="pw-heading">
            {t('profile.password.title')}
          </h3>
          <p className="muted pw-intro">{t('profile.password.intro', { phone: masked })}</p>
          {noticeBlock}
          <Button type="submit" loading={pending} disabled={waiting} data-testid="pw-send">
            {waiting ? t('profile.password.waitIn', { seconds: blockedIn }) : t('auth.forgot.phone.submit')}
          </Button>
        </form>
      )}

      {step === 'code' && (
        <form noValidate onSubmit={(e) => submit(e, () => code.length === 6 && flow.submitCode(code))}>
          <h3 ref={heading} tabIndex={-1} className="pw-heading">
            {t('auth.forgot.code.title')}
          </h3>
          <p className="muted pw-intro" data-testid="pw-sent">
            {t('auth.forgot.sent.neutral')}
          </p>
          <OTPInput value={code} onChange={setCode} disabled={flow.lockedOut} invalid={!!noticeText} autoFocus />
          {noticeBlock}
          <div className="pw-actions">
            <Button
              type="submit"
              loading={pending}
              disabled={code.length !== 6 || flow.lockedOut}
              data-testid="pw-verify"
            >
              {t('auth.forgot.code.submit')}
            </Button>
            <button
              type="button"
              className="pw-link"
              disabled={flow.resendIn > 0 || pending || waiting}
              onClick={flow.sendCode}
              data-testid="pw-resend"
            >
              {flow.resendIn > 0 ? t('auth.forgot.resendIn', { seconds: flow.resendIn }) : t('auth.forgot.resend')}
            </button>
          </div>
        </form>
      )}

      {step === 'password' && (
        <form noValidate onSubmit={(e) => submit(e, onPassword)}>
          <h3 ref={heading} tabIndex={-1} className="pw-heading">
            {t('auth.forgot.password.title')}
          </h3>
          <div className="pw-field-row">
            <Input
              label={t('auth.forgot.password.new')}
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              aria-describedby="pw-rules"
              data-testid="pw-new"
            />
            <button
              type="button"
              className="pw-toggle"
              aria-pressed={show}
              onClick={() => setShow((v) => !v)}
              data-testid="pw-toggle"
            >
              {show ? t('auth.forgot.password.hide') : t('auth.forgot.password.show')}
            </button>
          </div>
          <ul className="pw-rules" id="pw-rules" aria-live="polite" data-testid="pw-rules">
            {RULES.map((r) => (
              <li key={r.rule} data-ok={!violations.includes(r.rule)} data-testid={`pw-rule-${r.rule}`}>
                {t(r.label)}
              </li>
            ))}
            {violations.includes('NOT_PHONE') && (
              <li data-ok="false" data-testid="pw-rule-NOT_PHONE">
                {t('profile.password.ruleNotPhone')}
              </li>
            )}
          </ul>
          <div className="pw-strength" data-level={strength} data-testid="pw-strength">
            <span className="pw-strength-bar" aria-hidden />
            <span className="pw-strength-label">{t(`profile.password.strength.${strength}`)}</span>
          </div>
          <Input
            label={t('auth.forgot.password.confirm')}
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={mismatch ? t('auth.forgot.password.mismatch') : undefined}
            data-testid="pw-confirm"
          />
          {triedSubmit && violations.length > 0 && (
            <p className="text-danger pw-notice" role="alert" data-testid="pw-policy-error">
              {t('auth.forgot.error.policy')}
            </p>
          )}
          {noticeBlock}
          <Button type="submit" loading={pending} disabled={waiting} data-testid="pw-submit">
            {t('auth.forgot.password.submit')}
          </Button>
        </form>
      )}

      {step === 'done' && (
        <div>
          <h3 ref={heading} tabIndex={-1} className="pw-heading">
            {t('auth.forgot.done.title')}
          </h3>
          <p className="pw-success" role="status" data-testid="pw-done">
            {t('auth.forgot.done.body')}
          </p>
          <Button onClick={onSignOut} data-testid="pw-signin">
            {t('auth.forgot.done.cta')}
          </Button>
        </div>
      )}
    </div>
  );
}
